import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, RotateCcw, AlertCircle, CheckCircle2, RefreshCw, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { parsePastedTable, coerceCellValue } from "@/lib/parsePaste";
import { assignableRoles, defaultRoleFor, ROLE_LABELS } from "@/lib/roles";

const MAX_ROWS = 200;
const COLUMNS = [
  { key: "name", header: "Full Name", w: "min-w-[220px]" },
  { key: "email", header: "Email", w: "min-w-[280px]" },
  { key: "role", header: "Role", w: "w-[140px]", dropdown: "role" },
];
const STATUS_DOT = {
  create: { color: "bg-emerald-500 ring-emerald-100", label: "Will invite" },
  skip:   { color: "bg-slate-400 ring-slate-200", label: "Email already exists" },
  error:  { color: "bg-rose-500 ring-rose-100", label: "Has errors" },
};

const DRAFT_KEY = "kippu_users_bulk_draft_v1";

let _ridCounter = 0;
function emptyRow(role = "l1_agent") {
  _ridCounter += 1;
  return { _rid: `r-${Date.now()}-${_ridCounter}`, name: "", email: "", role };
}

export default function BulkInviteUsersDialog({ open, onOpenChange, onImported }) {
  const { user: caller } = useAuth();
  const { active } = useClient();
  const clientRoles = active?.roles || null;
  const defaultRole = defaultRoleFor(clientRoles);
  const [rows, setRows] = useState([emptyRow()]);
  const [validation, setValidation] = useState(null);
  const [partial, setPartial] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstCellRef = useRef(null);
  const rowRefs = useRef({});

  // Restore draft
  useEffect(() => {
    if (!open) return;
    try {
      const stored = localStorage.getItem(DRAFT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.some((r) => r.name || r.email)) setRows(parsed);
        else setRows([emptyRow(defaultRole)]);
      } else {
        setRows([emptyRow(defaultRole)]);
      }
    } catch (e) { /* localStorage failures (quota / private mode) are non-fatal */
        if (typeof console !== "undefined") console.warn("[bulk-dialog] localStorage:", e?.message || e);
      }
    // Auto-focus first empty cell after dialog mount
    setTimeout(() => firstCellRef.current?.focus?.(), 120);
  }, [open]);

  // Autosave
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(rows)); } catch (e) { /* localStorage failures (quota / private mode) are non-fatal */
        if (typeof console !== "undefined") console.warn("[bulk-dialog] localStorage:", e?.message || e);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [rows, open]);

  const availableRoles = useMemo(() => assignableRoles(caller), [caller]);

  const errorsByRow = useMemo(() => {
    if (!validation) return {};
    const m = {};
    for (const e of validation.errors || []) {
      if (e.row_index < 0) continue;
      (m[e.row_index] ||= {})[e.column] = e.message;
    }
    return m;
  }, [validation]);

  const statusByRow = useMemo(() => {
    if (!validation) return {};
    const m = {};
    for (const p of validation.preview || []) m[p.row_index] = p.status;
    return m;
  }, [validation]);

  const update = (i, k, v) => {
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    if (validation) setValidation(null);
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return toast.error(`Row limit (${MAX_ROWS}) reached`);
    setRows((prev) => [...prev, emptyRow(defaultRole)]);
  };

  const delRow = (i) => {
    setRows((prev) => prev.length === 1 ? [emptyRow(defaultRole)] : prev.filter((_, idx) => idx !== i));
    setValidation(null);
  };

  const clearAll = () => {
    if (rows.some((r) => r.name || r.email)) {
      if (!window.confirm("Clear all rows? Unsaved work in this grid will be lost.")) return;
    }
    setRows([emptyRow(defaultRole)]);
    setValidation(null);
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* localStorage failures (quota / private mode) are non-fatal */
        if (typeof console !== "undefined") console.warn("[bulk-dialog] localStorage:", e?.message || e);
      }
  };

  const onPaste = (e, targetRow, targetCol) => {
    const text = e.clipboardData?.getData("text");
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return;
    e.preventDefault();
    const { rows: pasted, hasHeader } = parsePastedTable(text);
    const dataRows = hasHeader ? pasted.slice(1) : pasted;
    if (!dataRows.length) return;

    const colIdx = COLUMNS.findIndex((c) => c.key === targetCol);
    if (colIdx < 0) return;

    setRows((prev) => {
      const next = [...prev];
      dataRows.forEach((vals, dy) => {
        const t = targetRow + dy;
        while (next.length <= t) next.push(emptyRow(defaultRole));
        if (t >= MAX_ROWS) return;
        const row = { ...next[t] };
        vals.forEach((cellVal, dx) => {
          const c = COLUMNS[colIdx + dx];
          if (!c) return;
          let v = (cellVal || "").trim();
          if (c.key === "role") {
            // Accept friendly inputs: "Admin", "L1 Agent", "supervisor" (legacy)
            v = coerceCellValue("priority", v)  // priority's lowercase coercion works generically
              .toLowerCase().replace(/[\s-]/g, "_");
            const legacy = { supervisor: "manager", viewer: "client" };
            // "agent" is a real role for lead-management clients only.
            if (v === "agent" && !availableRoles.includes("agent")) v = "l1_agent";
            // "agent" is a real role for lead-management clients only.
            if (v === "agent" && !availableRoles.includes("agent")) v = "l1_agent";
            v = legacy[v] || v;
          }
          row[c.key] = v;
        });
        next[t] = row;
      });
      return next.slice(0, MAX_ROWS);
    });
    toast.success(`Pasted ${Math.min(dataRows.length, MAX_ROWS - targetRow)} rows${hasHeader ? " · header skipped" : ""}`);
    setValidation(null);
  };

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk-invite-validate", { rows });
      setValidation(data);
      const s = data.summary;
      toast.success(`Validated · ${s.will_create} to invite · ${s.will_skip} skip · ${s.rows_invalid} error${s.rows_invalid === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk-invite", { rows, allow_partial: partial });
      toast.success(`Invited ${data.created_count} user${data.created_count === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* localStorage failures (quota / private mode) are non-fatal */
        if (typeof console !== "undefined") console.warn("[bulk-dialog] localStorage:", e?.message || e);
      }
      setRows([emptyRow(defaultRole)]);
      setValidation(null);
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      if (e?.response?.status === 422 && e.response.data?.detail) {
        setValidation(e.response.data.detail);
        toast.error("Validation errors — review red cells");
      } else {
        toast.error(e?.response?.data?.detail || "Bulk invite failed");
      }
    } finally { setBusy(false); }
  };

  const summary = validation?.summary;
  const canImport = rows.some((r) => r.name && r.email)
    && (!summary || summary.rows_invalid === 0 || partial);

  const firstErrorRow = useMemo(() => {
    const idxs = Object.keys(errorsByRow).map(Number).filter((n) => !Number.isNaN(n));
    return idxs.length ? Math.min(...idxs) : -1;
  }, [errorsByRow]);

  const scrollToFirstError = () => {
    if (firstErrorRow < 0) return;
    const el = rowRefs.current[firstErrorRow];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-rose-300");
      setTimeout(() => el.classList.remove("ring-2", "ring-rose-300"), 1400);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1100px] max-h-[92vh] flex flex-col" data-testid="bulk-invite-dialog">
        <DialogHeader className="pb-2">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users2 className="w-4 h-4 text-indigo-500" /> Bulk invite team members
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Paste rows of <strong>Name · Email · Role</strong> from your HR sheet, or type them manually. We'll skip emails that already exist and reject roles above your own hierarchy.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap bg-slate-50 border-y border-slate-200 px-2 py-2 -mx-6">
          <Button size="sm" variant="outline" onClick={clearAll} data-testid="bulk-invite-clear">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear all
          </Button>
          <Button size="sm" variant="outline" onClick={addRow} data-testid="bulk-invite-add-row">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add row
          </Button>
          <div className="ml-auto flex items-center gap-2.5 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={partial} onCheckedChange={(v) => setPartial(!!v)} data-testid="bulk-invite-partial" />
              <span className="text-slate-600">Allow partial</span>
            </label>
            <Button size="sm" variant="outline" onClick={validate} disabled={busy} data-testid="bulk-invite-validate-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Validate
            </Button>
            <Button size="sm" onClick={runImport} disabled={busy || !canImport}
                    className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-invite-import-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Invite{summary ? ` ${summary.will_create}` : ""}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto -mx-6 px-1 border-b border-slate-200">
          <table className="w-full text-xs" data-testid="users-bulk-grid">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-2 py-2 text-center w-10">#</th>
                <th className="px-2 py-2 text-center w-8" title="Status after Validate">●</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={`px-2 py-2 text-left font-semibold ${c.w}`}>{c.header}</th>
                ))}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const status = statusByRow[i];
                const dot = STATUS_DOT[status];
                const cellErrs = errorsByRow[i] || {};
                const hasErr = Object.keys(cellErrs).length > 0;
                return (
                  <tr key={row._rid || `r-${i}`}
                      ref={(el) => { if (el) rowRefs.current[i] = el; }}
                      className={`border-b border-slate-100 transition ${hasErr ? "bg-rose-50/40 hover:bg-rose-50/70" : "hover:bg-slate-50/60"}`}>
                    <td className="text-center text-slate-400 font-mono">{i + 1}</td>
                    <td className="text-center">
                      {dot && <span className={`inline-block w-2.5 h-2.5 rounded-full ring-2 ${dot.color}`} title={dot.label} />}
                    </td>
                    {COLUMNS.map((c, ci) => (
                      <Cell key={c.key} col={c} value={row[c.key]} error={cellErrs[c.key]}
                            availableRoles={availableRoles}
                            inputRef={i === 0 && ci === 0 ? firstCellRef : null}
                            onChange={(v) => update(i, c.key, v)}
                            onPaste={(e) => onPaste(e, i, c.key)} />
                    ))}
                    <td className="text-center">
                      <button onClick={() => delRow(i)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`bulk-invite-del-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={COLUMNS.length + 3}
                    className="text-center text-xs text-slate-400 py-2 hover:text-indigo-600 cursor-pointer hover:bg-slate-50"
                    onClick={addRow}>＋ Add row (or paste here)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="px-2 pt-2 -mx-6 text-xs">
          <div className="text-slate-600">
            <b>{rows.length}</b> rows
            {summary && (
              <>
                {" · "}<span className="text-emerald-600 font-semibold">{summary.will_create} invite</span>
                {" · "}<span className="text-slate-500 font-semibold">{summary.will_skip} skip (already exist)</span>
                {summary.rows_invalid > 0 && (
                  <>{" · "}<button type="button" onClick={scrollToFirstError}
                                   className="text-rose-600 font-semibold inline-flex items-center gap-1 hover:underline cursor-pointer"
                                   data-testid="bulk-invite-jump-errors">
                    <AlertCircle className="w-3 h-3" /> {summary.rows_invalid} error{summary.rows_invalid === 1 ? "" : "s"}
                  </button></>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ col, value, error, availableRoles, onChange, onPaste, inputRef }) {
  const ringClass = error
    ? "border-rose-300 bg-rose-50 ring-1 ring-rose-200"
    : "border-transparent hover:border-slate-200 focus-within:border-indigo-400 focus-within:bg-indigo-50/30";

  const td = `px-1 py-0.5 ${col.w}`;

  if (col.dropdown === "role") {
    const hasMatch = !value || availableRoles.includes(value);
    return (
      <td className={td}>
        <div className={`rounded border ${ringClass}`} title={error || ""}>
          <Select value={hasMatch ? (value || "l1_agent") : value} onValueChange={onChange}>
            <SelectTrigger className="h-7 text-xs border-none px-2">
              <SelectValue>{ROLE_LABELS[value] || value || "—"}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableRoles.map((r) => (
                <SelectItem key={r} value={r} className="text-xs">{ROLE_LABELS[r] || r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </td>
    );
  }

  return (
    <td className={td}>
      <input
        ref={inputRef || undefined}
        type={col.key === "email" ? "email" : "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        title={error || ""}
        placeholder={col.key === "email" ? "name@company.com" : col.key === "name" ? "Full name" : ""}
        className={`w-full h-7 px-2 text-xs rounded border bg-transparent outline-none ${ringClass}`}
        data-testid={`bulk-invite-cell-${col.key}`}
      />
    </td>
  );
}
